import React, { useState, useCallback } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Building2,
  ScrollText,
  Bot,
  CreditCard,
  ArrowLeft,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { clearAuthSession } from '@/lib/authToken'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/admin', icon: <LayoutDashboard size={18} /> },
  { label: 'Users', to: '/admin/users', icon: <Users size={18} /> },
  { label: 'Workspaces', to: '/admin/workspaces', icon: <Building2 size={18} /> },
  { label: 'Plans', to: '/admin/plans', icon: <CreditCard size={18} /> },
  { label: 'Activity', to: '/admin/logs', icon: <ScrollText size={18} /> },
  { label: 'AI Config', to: '/admin/ai-config', icon: <Bot size={18} /> },
]

const AdminSidebar: React.FC = () => {
  const { currentTheme, isDarkMode, setIsDarkMode } = useTheme()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [confirmLogout, setConfirmLogout] = useState(false)

  const handleLogout = useCallback(() => {
    clearAuthSession()
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const closeMobile = useCallback(() => setMobileOpen(false), [])

  const sidebarContent = (
    <div
      className="flex flex-col h-full"
      style={{ backgroundColor: currentTheme.sidebarBg }}
    >
      {/* Logo */}
      <div
        className="flex items-center gap-3 px-5 py-5 border-b"
        style={{ borderColor: currentTheme.sidebarBorder }}
      >
        <img
          src="/Zeph-White-Logo.png"
          alt="Zeph logo"
          className="object-contain w-8 h-8 shrink-0"
        />
        <span className="text-sm font-semibold" style={{ color: currentTheme.sidebarActiveText }}>
          Admin Portal
        </span>
      </div>

      {/* Nav items */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/admin'}
            onClick={closeMobile}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? 'active-nav' : ''}`
            }
            style={({ isActive }) => ({
              backgroundColor: isActive ? currentTheme.sidebarActive : 'transparent',
              color: isActive ? currentTheme.sidebarActiveText : currentTheme.sidebarText,
            })}
            onMouseEnter={(e) => {
              const el = e.currentTarget
              if (!el.classList.contains('active-nav')) {
                el.style.backgroundColor = currentTheme.sidebarHover
              }
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget
              if (!el.classList.contains('active-nav')) {
                el.style.backgroundColor = 'transparent'
              }
            }}
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}

        {/* Separator */}
        <div
          className="my-3 border-t"
          style={{ borderColor: currentTheme.sidebarBorder }}
        />

        <button
          type="button"
          onClick={() => { navigate('/dashboard'); closeMobile() }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ color: currentTheme.sidebarText, backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.sidebarHover
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
          }}
        >
          <ArrowLeft size={18} />
          Back to App
        </button>
      </nav>

      {/* Theme toggle + Logout */}
      <div
        className="px-3 py-4 border-t space-y-1"
        style={{ borderColor: currentTheme.sidebarBorder }}
      >
        <button
          type="button"
          onClick={() => setIsDarkMode(!isDarkMode)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ color: currentTheme.sidebarText, backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.sidebarHover
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
          }}
        >
          {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          {isDarkMode ? 'Light mode' : 'Dark mode'}
        </button>
        <button
          type="button"
          onClick={() => setConfirmLogout(true)}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all"
          style={{ color: currentTheme.sidebarText, backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.sidebarHover
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
          }}
        >
          <LogOut size={18} />
          Log out
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-40 inline-flex items-center justify-center w-10 h-10 rounded-xl shadow-lg lg:hidden"
        style={{
          backgroundColor: currentTheme.sidebarBg,
          color: currentTheme.sidebarText,
          border: `1px solid ${currentTheme.sidebarBorder}`,
        }}
        aria-label="Open sidebar"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={closeMobile}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={closeMobile}
          className="absolute right-3 top-3 z-10 inline-flex items-center justify-center w-8 h-8 rounded-lg"
          style={{ color: currentTheme.sidebarText }}
          aria-label="Close sidebar"
        >
          <X size={18} />
        </button>
        {sidebarContent}
      </div>

      {/* Desktop fixed sidebar */}
      <div
        className="hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:w-56 lg:z-30"
      >
        {sidebarContent}
      </div>

      <Dialog open={confirmLogout} onOpenChange={setConfirmLogout}>
        <DialogContent
          className="py-6 px-8 rounded-2xl shadow-xl max-w-sm w-full border-none"
          style={{ background: currentTheme.secondary, color: currentTheme.text }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-center" style={{ color: currentTheme.text }}>
              Log out of the admin portal?
            </DialogTitle>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-3 mt-6 flex-row sm:justify-center">
            <Button
              variant="secondary"
              onClick={() => setConfirmLogout(false)}
              className="px-5 py-2 rounded-lg font-medium h-auto text-sm"
              style={{ background: currentTheme.hoverBackground, color: currentTheme.text, border: `1px solid ${currentTheme.border}` }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLogout}
              className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium h-auto text-sm"
            >
              Log out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default React.memo(AdminSidebar)
